/**
 * EngineHost owns the single PlayCanvas application and its canvas. The gallery preview and the
 * entered world share it, so entering a world never re-creates the GPU context.
 *
 * Responsibilities: device creation (WebGL2), canvas sizing with a capped pixel ratio, an
 * optional render cap, hidden-tab behaviour, context loss/restore notifications, and frame
 * timing used by the adaptive quality governor.
 *
 * The picture must never stay frozen while the page is visible. A watchdog (on a timer, which
 * keeps running when animation frames stop) handles the three ways it can:
 * - the frame loop stalls (the browser stops delivering animation frames): it is ticked from the
 *   timer, which also asks for animation frames again;
 * - the WebGL context is lost and the browser does not restore it (after a GPU reset the browser
 *   may refuse to): after LOST_GIVE_UP_MS the host reports a failure, and the shell rebuilds the
 *   graphics on a fresh canvas. iOS releases a backgrounded page's graphics and does not always
 *   say so: the context is checked directly (`isContextLost`), not only through the lost event, and
 *   a loss in the background (or just after returning) is reported as such: it says nothing about
 *   whether the device copes, so the shell need not lower the detail;
 * - every frame's rendering throws: reported the same way after RENDER_GIVE_UP_MS.
 * On returning from the background and after a restore, the canvas's compositing layer is nudged
 * for a frame: WebKit has shown a restored context only after the next layout.
 * An exception in one update callback is logged (throttled) and never stops the others or the
 * render.
 */
import * as pc from 'playcanvas';

export interface FrameInfo {
  /** Real seconds since previous update (clamped). */
  dt: number;
  /** Whether this update will be followed by a render. */
  rendering: boolean;
  /** Milliseconds between the last two rendered frames (for the governor). */
  frameMs: number;
  /**
   * Whether frameMs is a fair measure of the device: false for the first frame after a pause or a
   * hidden page, and for a frame that compiled shaders (a level change, a settings change, a new
   * effect): those hitches are one-offs, not the device's pace.
   */
  steady: boolean;
}

type UpdateFn = (info: FrameInfo) => void;
export type GraphicsFailure = 'context-lost' | 'context-lost-in-background' | 'render-failing';

/** How long a lost context may wait for the browser to restore it (visible time). */
const LOST_GIVE_UP_MS = 6000;
/** The same for a context lost while the page was in the background (iOS frees them). */
const LOST_IN_BACKGROUND_GIVE_UP_MS = 3000;
/** A loss this soon after the page returns is taken as the background's doing. */
const BACKGROUND_GRACE_MS = 5000;
/** How long every requested frame may fail to render before the graphics are rebuilt. */
const RENDER_GIVE_UP_MS = 4000;
/** No frame for this long while visible means the frame loop has stalled. */
const STALL_MS = 1500;
const WATCHDOG_MS = 500;

export class EngineHost {
  readonly app: pc.AppBase;
  readonly device: pc.GraphicsDevice;
  readonly canvas: HTMLCanvasElement;

  private updateFns = new Set<UpdateFn>();
  private restoreFns = new Set<() => void>();
  private lostFns = new Set<() => void>();
  private failureFns = new Set<(reason: GraphicsFailure) => void>();
  private maxPixelRatio = 1.5;
  private renderScale = 1;
  private frameCap: 30 | 60 | null = null;
  private lastRenderAt = 0;
  private lastFrameMs = 16.7;
  private renderAccumulator = 0;
  private resizeObserver: ResizeObserver;
  private destroyed = false;
  private paused = false;
  /** Whether the previous tick was kept from rendering by a pause or a hidden page. */
  private blocked = true;
  private lastTickAt = performance.now();
  /** Visible time the context has been lost for (ms), and the clock it was last counted at. */
  private lostFor = 0;
  private lostCountedAt = 0;
  /** Whether the current loss has been noticed, and whether it came with the background. */
  private lostNoted = false;
  private lostInBackground = false;
  /** Visibility as last seen, and when the page last came back from the background. */
  private wasHidden = document.hidden;
  private shownAt = -Infinity;
  /** Shader count at the last tick, and whether shaders were compiled since the last render. */
  private shaderCount = 0;
  private compiledSinceRender = false;
  /** A render was requested and has not completed (set in tick, cleared after the render). */
  private renderPending = false;
  private renderFailingSince: number | null = null;
  private failed = false;
  private started = false;
  private watchdogTimer = 0;
  private errorCount = 0;
  private lastErrorLogAt = -Infinity;
  /** Times the watchdog had to tick a stalled frame loop (diagnostics). */
  stalls = 0;

  private constructor(canvas: HTMLCanvasElement, device: pc.GraphicsDevice) {
    this.canvas = canvas;
    this.device = device;
    const opts = new pc.AppOptions();
    opts.graphicsDevice = device;
    opts.componentSystems = [pc.RenderComponentSystem, pc.CameraComponentSystem, pc.LightComponentSystem];
    opts.resourceHandlers = [pc.TextureHandler];
    const app = new pc.AppBase(canvas);
    app.init(opts);
    app.setCanvasFillMode(pc.FILLMODE_NONE);
    app.setCanvasResolution(pc.RESOLUTION_FIXED);
    app.autoRender = false;
    app.maxDeltaTime = 0.1;
    this.app = app;

    app.on('update', (dt: number) => this.tick(dt));
    app.on('postrender', () => {
      this.renderPending = false;
      this.renderFailingSince = null;
    });
    device.on('devicelost', () => this.noteLost());
    device.on('devicerestored', () => {
      console.warn('[GBR] graphics context restored');
      this.lostFor = 0;
      this.lostNoted = false;
      this.renderPending = false;
      this.renderFailingSince = null;
      this.restoreFns.forEach((fn) => this.guard(fn));
      this.nudgeCompositing();
    });
    this.watchdogTimer = window.setInterval(() => this.watchdog(), WATCHDOG_MS);
    document.addEventListener('visibilitychange', this.noteVisibility);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    this.resize();
  }

  /** Returns null when WebGL2 is unavailable, so the shell can show a truthful fallback. */
  static async create(canvas: HTMLCanvasElement): Promise<EngineHost | null> {
    if (!EngineHost.supportsWebGL2()) return null;
    try {
      const device = await pc.createGraphicsDevice(canvas, {
        deviceTypes: [pc.DEVICETYPE_WEBGL2],
        antialias: false,
        alpha: false,
        depth: true,
        stencil: false,
        powerPreference: 'default',
      });
      return new EngineHost(canvas, device);
    } catch (err) {
      console.warn('[GBR] graphics device creation failed', err);
      return null;
    }
  }

  static supportsWebGL2(): boolean {
    try {
      const c = document.createElement('canvas');
      const gl = c.getContext('webgl2');
      const ok = !!gl;
      gl?.getExtension('WEBGL_lose_context')?.loseContext();
      return ok;
    } catch {
      return false;
    }
  }

  start(): void {
    this.started = true;
    this.lastTickAt = performance.now();
    this.app.start();
  }

  onUpdate(fn: UpdateFn): () => void {
    this.updateFns.add(fn);
    return () => this.updateFns.delete(fn);
  }

  /**
   * Called once if the graphics cannot recover in place (a lost context the browser will not
   * restore, or rendering that fails every frame). The owner should destroy this host and build a
   * new one on a fresh canvas: a canvas whose context was lost for good cannot get another.
   */
  onFailure(fn: (reason: GraphicsFailure) => void): () => void {
    this.failureFns.add(fn);
    return () => this.failureFns.delete(fn);
  }

  /** Called when the WebGL context is lost (the canvas shows nothing until it is restored or rebuilt). */
  onLost(fn: () => void): () => void {
    this.lostFns.add(fn);
    return () => this.lostFns.delete(fn);
  }

  /** Called after a lost WebGL context is restored (render-target contents must be rebuilt). */
  onRestore(fn: () => void): () => void {
    this.restoreFns.add(fn);
    return () => this.restoreFns.delete(fn);
  }

  setMaxPixelRatio(ratio: number): void {
    if (Math.abs(ratio - this.maxPixelRatio) < 1e-3) return;
    this.maxPixelRatio = ratio;
    this.resize();
  }

  /** Multiplier on the backing-store size (applied on top of the pixel-ratio cap). */
  setRenderScale(scale: number): void {
    if (Math.abs(scale - this.renderScale) < 1e-3) return;
    this.renderScale = scale;
    this.resize();
  }

  setFrameCap(cap: 30 | 60 | null): void {
    this.frameCap = cap;
  }

  /** Suspend rendering (simulation updates continue unless the world itself pauses). */
  setPaused(paused: boolean): void {
    this.paused = paused;
  }

  get pixelSize(): { width: number; height: number } {
    return { width: this.canvas.width, height: this.canvas.height };
  }

  get effectivePixelRatio(): number {
    return Math.min(window.devicePixelRatio || 1, this.maxPixelRatio) * this.renderScale;
  }

  private resize(): void {
    if (this.destroyed) return;
    const rect = this.canvas.getBoundingClientRect();
    let ratio = this.effectivePixelRatio;
    // Supersampling on a large display could exceed what the GPU can allocate: keep the long
    // side within the device's limit (and 8K).
    const limit = Math.min(8192, this.device.maxTextureSize || 8192);
    const long = Math.max(rect.width, rect.height) * ratio;
    if (long > limit) ratio *= limit / long;
    const w = Math.max(2, Math.round(rect.width * ratio));
    const h = Math.max(2, Math.round(rect.height * ratio));
    if (w !== this.canvas.width || h !== this.canvas.height) {
      this.app.setCanvasResolution(pc.RESOLUTION_FIXED, w, h);
    }
  }

  private tick(dt: number): void {
    const now = performance.now();
    this.lastTickAt = now;
    this.noteVisibility();
    // A render requested last tick that never completed: the render path threw.
    if (this.renderPending) this.renderFailingSince ??= now;
    // New shaders since the last tick were compiled in its render: that frame is a hitch.
    const shaders = this.device.shaders.length;
    if (shaders !== this.shaderCount) {
      this.shaderCount = shaders;
      this.compiledSinceRender = true;
    }
    const showing = !this.paused && !document.hidden;
    let render = showing;
    // The first frame after a pause or a hidden page is not a slow frame: leave the gap out of
    // the frame timing the governor sees.
    if (this.blocked) this.lastRenderAt = 0;
    this.blocked = !showing;
    if (render && this.frameCap !== null) {
      // Skip display frames to hold the cap (every other frame for 30 on a 60 Hz display, or for
      // 60 on a 120 Hz one); tolerate jitter.
      const period = 1 / this.frameCap;
      this.renderAccumulator += dt;
      if (this.renderAccumulator < period - 0.004) render = false;
      else this.renderAccumulator = Math.min(this.renderAccumulator - period, period);
    }
    let steady = false;
    if (render) {
      if (this.lastRenderAt > 0) {
        this.lastFrameMs = now - this.lastRenderAt;
        steady = !this.compiledSinceRender;
      }
      this.lastRenderAt = now;
      this.compiledSinceRender = false;
    }
    const info: FrameInfo = { dt, rendering: render, frameMs: this.lastFrameMs, steady };
    for (const fn of this.updateFns) this.guard(() => fn(info));
    this.app.renderNextFrame = render;
    this.renderPending = render;
  }

  /** Run a callback; an exception is logged (the first in full, then a count now and then). */
  private guard(fn: () => void): void {
    try {
      fn();
    } catch (err) {
      this.errorCount++;
      const now = performance.now();
      if (this.errorCount === 1 || now - this.lastErrorLogAt > 10_000) {
        this.lastErrorLogAt = now;
        console.error(`[GBR] frame update failed (${this.errorCount} so far); the picture carries on`, err);
      }
    }
  }

  /** The context is gone: as the engine was told, or as WebGL itself says (iOS may not tell). */
  private get contextLost(): boolean {
    if (this.device.contextLost) return true;
    try {
      return (this.device as pc.WebglGraphicsDevice).gl?.isContextLost() ?? false;
    } catch {
      return false;
    }
  }

  private noteLost(): void {
    if (this.lostNoted) return;
    this.lostNoted = true;
    const now = performance.now();
    this.lostFor = 0;
    this.lostCountedAt = now;
    this.lostInBackground = document.hidden || now - this.shownAt < BACKGROUND_GRACE_MS;
    console.warn(`[GBR] graphics context lost${this.lostInBackground ? ' in the background' : ''}; waiting for the browser to restore it`);
    this.lostFns.forEach((fn) => this.guard(fn));
  }

  /** Track returns from the background (from the event, and checked each tick and watchdog run). */
  private noteVisibility = (): void => {
    if (document.hidden) {
      this.wasHidden = true;
    } else if (this.wasHidden) {
      this.wasHidden = false;
      this.shownAt = performance.now();
      this.nudgeCompositing();
    }
  };

  /**
   * Change the canvas's compositing for a couple of frames, so the browser reconfigures its layer:
   * WebKit has shown a restored context's drawing only after the next layout. Harmless elsewhere.
   */
  private nudgeCompositing(): void {
    if (this.destroyed) return;
    const style = this.canvas.style;
    style.transform = 'translateZ(0)';
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        style.transform = '';
      }),
    );
  }

  private watchdog(): void {
    if (this.destroyed || this.failed || !this.started) return;
    const now = performance.now();
    this.noteVisibility();
    if (document.hidden) {
      this.lostCountedAt = now;
      this.renderFailingSince = null;
      return;
    }
    if (this.contextLost) {
      this.noteLost();
      this.lostFor += now - this.lostCountedAt;
      this.lostCountedAt = now;
      if (this.lostFor > (this.lostInBackground ? LOST_IN_BACKGROUND_GIVE_UP_MS : LOST_GIVE_UP_MS)) this.fail(this.lostInBackground ? 'context-lost-in-background' : 'context-lost');
      return;
    }
    if (this.renderFailingSince !== null && now - this.renderFailingSince > RENDER_GIVE_UP_MS) {
      this.fail('render-failing');
      return;
    }
    // The frame loop stalled while the page is visible: tick it from here (which also requests
    // the next animation frame), so the picture keeps moving even if only slowly.
    if (now - this.lastTickAt > STALL_MS) {
      this.stalls++;
      if (this.stalls === 1 || this.stalls % 20 === 0) console.warn(`[GBR] frame loop stalled; ticking it from a timer (${this.stalls})`);
      this.app.tick(now);
    }
  }

  private fail(reason: GraphicsFailure): void {
    this.failed = true;
    console.error(`[GBR] graphics could not recover in place (${reason}); rebuilding them`);
    for (const fn of this.failureFns) this.guard(() => fn(reason));
  }

  destroy(): void {
    this.destroyed = true;
    window.clearInterval(this.watchdogTimer);
    document.removeEventListener('visibilitychange', this.noteVisibility);
    this.resizeObserver.disconnect();
    this.updateFns.clear();
    this.restoreFns.clear();
    this.lostFns.clear();
    this.failureFns.clear();
    this.app.destroy();
    // Hand the GPU memory back now rather than whenever the old canvas is collected.
    try {
      this.canvas.getContext('webgl2')?.getExtension('WEBGL_lose_context')?.loseContext();
    } catch {
      /* already lost */
    }
  }
}
